import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { SessionCompany } from '@core/auth/auth-session.model';
import { userOwnsAnyCompany } from '@core/auth/company-membership-label.util';
import { MaterialModule } from 'src/app/material.module';
import { WorkspaceCompanyCardComponent } from '@core/shell/workspace-company-card/workspace-company-card.component';

export interface WorkspacePickerDialogData {
  companies: SessionCompany[];
  activeCompanyId: number | null;
  allowCreateBusiness?: boolean;
}

export type WorkspacePickerDialogResult =
  | { action: 'switch'; companyId: number }
  | { action: 'create_business' }
  | null;

@Component({
  selector: 'app-workspace-picker-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MaterialModule,
    TranslateModule,
    TablerIconsModule,
    WorkspaceCompanyCardComponent,
  ],
  templateUrl: './workspace-picker-dialog.component.html',
  styleUrl: './workspace-picker-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkspacePickerDialogComponent {
  readonly data = inject<WorkspacePickerDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject(MatDialogRef<WorkspacePickerDialogComponent, WorkspacePickerDialogResult>);

  readonly alreadyOwnsBusiness = userOwnsAnyCompany(this.data.companies);

  readonly createTitleKey = this.alreadyOwnsBusiness
    ? 'header.workspacePicker.createAnotherBusiness'
    : 'header.workspacePicker.openOwnBusiness';

  readonly createHintKey = this.alreadyOwnsBusiness
    ? 'header.workspacePicker.createAnotherBusinessHint'
    : 'header.workspacePicker.openOwnBusinessHint';

  isActive(companyId: number): boolean {
    return this.data.activeCompanyId === companyId;
  }

  pickCompany(company: SessionCompany): void {
    if (this.isActive(company.id)) {
      this.dialogRef.close(null);
      return;
    }
    this.dialogRef.close({ action: 'switch', companyId: company.id });
  }

  openOwnBusiness(): void {
    this.dialogRef.close({ action: 'create_business' });
  }

  close(): void {
    this.dialogRef.close(null);
  }
}
